import { GqlClient } from "@/services/graph-ql/configs";
import { DataClientError } from "./errors";

export class BaseCRMAdapter {
  /**
   * Internal helper for GraphQL queries with unified error handling.
   * @param {Object} params - Query parameters, variables, headers, etc.
   * @returns {Promise<Object>} - The result data.
   */
  async _query(params) {
    const { 
      query, 
      clientName = "", 
      headers = {}, 
      fetchPolicy = "network-only",
      ...variables 
    } = params;

    try {
      const result = await GqlClient.query({
        query,
        variables,
        fetchPolicy,
        context: { 
          clientName, 
          headers 
        }
      });
      return result.data;
    } catch (error) {
      throw new DataClientError("graphql", 500, error.message, error);
    }
  }

  /**
   * SignalR Bridge for Mutations
   * Wraps the SignalR invoke and response listener into a Promise.
   * @param {Object} params - Mutation params including hub, method names, and payload.
   * @param {string|null} dataKey - Key to wrap the payload in (e.g., 'appointment', 'calendar').
   * @returns {Promise<Object>}
   */
  async _bridgeMutation(params, dataKey = "calendar") {
    const { hub, appointmentHub, methodName, responseMethodName, ...payload } = params;
    const activeHub = hub || appointmentHub;
    
    if (!activeHub || !methodName || !responseMethodName) {
      throw new Error(`SignalR 'hub', 'methodName', and 'responseMethodName' are required for CRM mutations.`);
    }

    return new Promise((resolve, reject) => {
      const requestPayload = dataKey ? { [dataKey]: payload } : payload;
      
      const handler = (response) => {
        // Cleanup the exact listener we just added
        activeHub.off(responseMethodName, handler); 
        
        if (response.isSuccess) {
          resolve(response);
        } else {
          const msg = response.errors && response.errors.length > 0 ? response.errors[0] : "SignalR mutation failed";
          reject(new Error(msg));
        }
      };

      activeHub.on(responseMethodName, handler);
      
      activeHub.invoke(methodName, requestPayload).catch(err => {
        activeHub.off(responseMethodName, handler);
        reject(new DataClientError("signalr", 500, err.message, err));
      });
    });
  }
}
